import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"

const GAP = 8
const SIDE = 12
const VIEWPORT_MARGIN = 8

const ImportantInfoBadge = ({
  infos = [],
  label = "Important info",
  title = "Important information",
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [pos, setPos] = useState({ above: false, top: 0, bottom: 0 })

  const wrapRef = useRef(null)
  const triggerRef = useRef(null)
  const tipRef = useRef(null)
  const closeTimer = useRef(null)
  const id = useId()

  useEffect(() => {
    onOpenChange?.(open)
  }, [open, onOpenChange])

  const cancelClose = () => clearTimeout(closeTimer.current)

  const show = () => {
    cancelClose()
    setOpen(true)
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => {
      if (!pinned) setOpen(false)
    }, 120)
  }

  const closeAll = useCallback(() => {
    clearTimeout(closeTimer.current)
    setPinned(false)
    setOpen(false)
  }, [])

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    const tip = tipRef.current
    const card = trigger?.offsetParent
    if (!trigger || !tip || !card) return

    const r = trigger.getBoundingClientRect()
    const th = tip.offsetHeight

    const fitsBelow = r.bottom + GAP + th <= window.innerHeight - VIEWPORT_MARGIN
    const fitsAbove = r.top - GAP - th >= VIEWPORT_MARGIN

    setPos({
      above: !fitsBelow && fitsAbove,
      top: trigger.offsetTop + trigger.offsetHeight + GAP,
      bottom: card.clientHeight - trigger.offsetTop + GAP,
    })
  }, [])

  useLayoutEffect(() => {
    if (open) updatePosition()
  }, [open, infos, updatePosition])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeAll()
        triggerRef.current?.focus()
      }
    }
    const onPointerDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return
      closeAll()
    }

    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("pointerdown", onPointerDown)
    window.addEventListener("resize", updatePosition)

    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("pointerdown", onPointerDown)
      window.removeEventListener("resize", updatePosition)
    }
  }, [open, closeAll, updatePosition])

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  if (!infos.length) return null

  const handleClick = () => {
    if (pinned) {
      closeAll()
    } else {
      setPinned(true)
      show()
    }
  }

  return (
    <div ref={wrapRef} className="inline-block" onMouseEnter={show} onMouseLeave={scheduleClose}>
      <button
        ref={triggerRef}
        type="button"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onFocus={show}
        onBlur={() => !pinned && scheduleClose()}
        onClick={handleClick}
        className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 transition-colors cursor-help"
      >
        <InfoIcon />
        {label}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="important-info"
            ref={tipRef}
            id={id}
            role="tooltip"
            initial={{ opacity: 0, y: pos.above ? -4 : 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: pos.above ? -4 : 4 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            style={{
              left: SIDE,
              ...(pos.above ? { bottom: pos.bottom } : { top: pos.top }),
              width: "max-content",
              minWidth: `min(288px, calc(100% - ${SIDE * 2}px))`,
              maxWidth: `calc(100% - ${SIDE * 2}px)`,
            }}
            className="absolute z-30 max-h-72 overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
          >
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1.5">
              <InfoIcon />
              {title}
            </p>
            <div className="flex flex-col divide-y divide-gray-100">
              {infos.map((text, i) => (
                <p
                  key={i}
                  className="text-xs leading-relaxed text-app-content whitespace-pre-line break-words py-1.5 first:pt-0 last:pb-0"
                >
                  {text}
                </p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const InfoIcon = () => (
  <svg
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" strokeWidth={2} />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v5M12 8h.01" />
  </svg>
)

export default ImportantInfoBadge
